import React, { useEffect, useState, useRef, memo } from "react";
import { TableCell, TableRow, Typography, Select, MenuItem, TextField, IconButton, Chip, Box, InputAdornment, Tooltip } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import QuantityControl from "../../../common/QuantityControl";
import productSplitService from "../../../../services/ProductSplitService";
import { calculateItemVat, parseQty, allowsDecimalQty, isMeatsProduct, resolveCartLineDisplayTotal, resolveCartLineInlinePromoGrossDisplayTotal, resolveCartLineStrikethroughPrice, resolveProductTaxCode, shouldShowCartLineInlinePromoDiscount, resolveCartLineInlinePromoDiscountForDisplay, shouldShowCartPromotionSummarySubRow, resolveCartPromotionSummaryBuyDiscountForDisplay, resolveCartPromotionSummarySubRowCount, resolveBuyQuantityForCartPromoGroup, resolveCartPromoStickySetsForItem, getCartLineDiscountComment } from "../../Epos/utils/cartCalculations";
import { shouldSkipTelecallerStockLimit } from "../../Epos/utils/telecallerStockLimit";
import { getQtyControlMax } from "../../Epos/utils/cartQtyValidation";
import { PREFIX, retainsCartDiscountFocus } from "../../../common/Config";
import { resolveIsPriceEditable } from "../../../../utils/companySettingsCache";
import { useCompanySettings } from "../../../../contexts/CompanySettingsContext";
import { getCartTableKeyboardHighlightSx } from "../../Epos/utils/cartTableKeyboard";
import { isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";

const DISABLE_DISC_COLUMN_WHEN_PROMOTED = true;

const CartTableRowEposSales = ({
  item,
  index,
  onTypeChange,
  onQuantityChange,
  onPriceChange,
  onDiscountChange,
  onDelete,
  showVatAsAmount = false,
  isLoading = false,
  itemDetails = null,
  onValidationError = null,
  customerId = null,
  onAllotQtyChange = null,
  onDiscountChangeComplete = null,
  onDiscountCommentChange = null,
  autoOpenQuantityPopup = false,
  onQuantityPopupOpen = null,
  onQuantityPopupClose = null,
  enableProductSplit = false,
  isTelecaller = false,
  isRhp = false,
  onPromoQtyLineOpenDrawer = null,
  onPromoQtyOpenPromotionsModal = null,
  isKeyboardHighlighted = false,
  onQtyRegionFocusChange = null,
  cartItemsForQtyMax = null,
  bundleDiscounts = [],
  bundleLineDiscountByKey = null,
}) => {
  const code = item.code || item.product_code || "";
  const description = item.desc || item.name || item.product_name || "";
  const promotion_name = item.promotion_name || item.promotionName || item.promo_name || "";
  const normalizeOrderType = (val) => {
    const raw = String(val ?? "Box").trim().toLowerCase();
    if (raw === "piece" || raw === "p" || raw === "1") return "Piece";
    if (raw === "box" || raw === "b" || raw === "0") return "Box";
    return raw.startsWith("p") ? "Piece" : "Box";
  };
  const orderType = normalizeOrderType(item.type || item.order_type || "Box");
  const qty = (item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== "")
    ? parseQty(item, item.order_qty)
    : parseQty(item, item.qty);
  const currentPrice = parseFloat(item.price) || 0;
  const discount = (() => {
    const overrideDiscount = item.manualOverrides?.discount;
    if (overrideDiscount !== undefined && overrideDiscount !== null && String(overrideDiscount).trim() !== "") {
      const rawValue = parseFloat(overrideDiscount);
      if (!Number.isFinite(rawValue) || rawValue < 0) {
        return 0;
      }
      return rawValue;
    }
    if (item.discount !== undefined && item.discount !== null && item.discount !== "") {
      const rawValue = parseFloat(item.discount);
      if (Number.isFinite(rawValue) && rawValue >= 0) {
        return rawValue;
      }
    }
    const apiItemDiscount = item.item_discount !== undefined && item.item_discount !== null && item.item_discount !== "" ? parseFloat(item.item_discount) : null;
    if (apiItemDiscount !== null && Number.isFinite(apiItemDiscount) && apiItemDiscount >= 0) {
      return apiItemDiscount;
    }
    const itemDiscount = item.productWiseDiscountValue;
    const rawValue = parseFloat(itemDiscount !== undefined && itemDiscount !== null ? itemDiscount : 0);
    if (!Number.isFinite(rawValue) || rawValue < 0) {
      return 0;
    }
    return rawValue;
  })();
  const rawVatRate = parseFloat(item.tax_rate ?? item.rate ?? "");
  const vatRate = Number.isFinite(rawVatRate) && rawVatRate >= 0 ? rawVatRate : 0;
  const isContractPrice = item.isContractPriceApplied === "Y";

  const canSplit = (() => {
    const splitVal = item.split !== undefined && item.split !== null ? parseInt(String(item.split), 10) : 0;
    if (splitVal === 2) return true;
    if (enableProductSplit && customerId && item.product_id) {
      return productSplitService.canSplit(customerId, item.product_id);
    }
    return false;
  })();

  const isPromotedFlag =
    item.is_promoted === "1" ||
    item.is_promoted === 1 ||
    item.is_promoted === true;

  const originalPrice = (() => {
    if (item.useBackendCalculations) {
      if (isPromotedFlag && item.original_price !== undefined && item.original_price > 0) {
        return parseFloat(item.original_price);
      }
      if (item.base_unit_price !== undefined && item.base_unit_price > 0) {
        return parseFloat(item.base_unit_price);
      }
    }
    return orderType === "Box"
      ? parseFloat(item.sales_box_price || 0)
      : parseFloat(item.sales_piece_price || 0);
  })();

  const itemDiscountType = item.discount_type || item.manualOverrides?.discount_type || item.productWiseDiscountType || "amount";
  const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
  const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";
  
  // Check if discount is 100% and PREFIX is "tsc" for red text styling
  const is100PercentDiscount = !isFixedDiscount && (discount === 100 || discount === 100.0);
  const shouldShowRedText = PREFIX === "tsc" && is100PercentDiscount;

  const actualPrice = item.useBackendCalculations && item.unit_price !== undefined
    ? parseFloat(item.unit_price)
    : currentPrice;
  const discountPercentage = parseFloat(item.discount_percentage || 0);
  const hasPriceDifference = originalPrice > 0 && Math.abs(originalPrice - actualPrice) > 0.01;
  const shouldShowOriginalPrice = (discount > 0 || discountPercentage > 0 || hasPriceDifference) && originalPrice > 0 && hasPriceDifference;

  const isFree = itemDetails?.isFree || false;
  const isGetPromotion = itemDetails?.isGetPromotion || false;
  const normalizedPromoType = (() => {
    let t = String(item.promo_type ?? item.promoType ?? item.promosType ?? "").trim().toUpperCase();
    if (!t && item.buyOrGet) {
      const b = String(item.buyOrGet).trim().toUpperCase();
      if (b === "BUY" || b === "GET" || b === "FREE") t = b;
    }
    return t;
  })();
  const isPromoTypeBuy = normalizedPromoType === "BUY";
  const isGetPromoType = normalizedPromoType === "GET";
  const isPromoTypeFree = normalizedPromoType === "FREE";
  const isGenerated = item.generated === true || item.generated === "1" || item.generated === 1;
  const isOfferLineDisplay = isCartLineOfferDisplayRole(item);
  const isGetPromotionResolved =
    isGetPromotion || (isPromotedFlag && isGetPromoType);
  const isFreeItem = isOfferLineDisplay && !isGetPromoType;
  const isItemPromoted =
    isPromotedFlag ||
    !!(item.promo_id || item.promoId || item.promosId || item.promotion_id || item.promotionId);
  const isPromotionApplied = item.is_promoted === 0;
  const promotionApplied =
    item.promotion_applied === true ||
    item.promotion_applied === "true" ||
    item.promotion_applied === 1 ||
    item.promotion_applied === "1";
  const promotionIdRaw = item.promotion_id ?? item.promotionId ?? item.promosId;
  const hasValidPromotionId =
    promotionIdRaw !== undefined &&
    promotionIdRaw !== null &&
    promotionIdRaw !== "" &&
    Number.isFinite(Number(promotionIdRaw)) &&
    Number(promotionIdRaw) > 0;
  const promotionExplicitlyRemoved =
    item.promotion_applied === false ||
    item.promotion_applied === "false" ||
    item.promotion_applied === 0 ||
    item.promotion_applied === "0";
  const hasPromoDiscountAmount =
    parseFloat(item.promo_disc_percentage ?? 0) > 0 ||
    parseFloat(item.promo_disc_amount ?? 0) > 0;
  const hasManualDiscountForPromoIndicator =
    (item.manualOverrides?.discount !== undefined &&
      item.manualOverrides?.discount !== null &&
      parseFloat(String(item.manualOverrides.discount || 0)) > 0) ||
    (!promotionApplied && discount > 0 && !hasPromoDiscountAmount);
  const hasStalePromoMetadataShape =
    isItemPromoted ||
    hasValidPromotionId ||
    normalizedPromoType === "BUY" ||
    normalizedPromoType === "GET";
  const suppressPromoIndicator =
    promotionExplicitlyRemoved ||
    (!promotionApplied &&
      !hasPromoDiscountAmount &&
      hasManualDiscountForPromoIndicator &&
      hasStalePromoMetadataShape);
  const showPromoIndicator =
    !suppressPromoIndicator &&
    (promotionApplied || hasValidPromotionId || isItemPromoted);
  const promotionNameTrimmed = String(promotion_name || "").trim();
  const showPromotionNameLabel =
    showPromoIndicator && !!promotionNameTrimmed && item.showPromo === true;
  const promoSummaryOptions = {
    cartItems: cartItemsForQtyMax,
  };
  const promoBundleDiscountOptions = {
    cartItems: cartItemsForQtyMax,
    distributeBundleAmongBuyLines: isTelecaller,
    lineDiscountByKey: bundleLineDiscountByKey,
    buyQuantity: resolveBuyQuantityForCartPromoGroup(item, cartItemsForQtyMax, bundleDiscounts),
    stickySets: resolveCartPromoStickySetsForItem(item, cartItemsForQtyMax, bundleDiscounts),
    ...promoSummaryOptions,
  };
  const showPromotionSummarySubRow = shouldShowCartPromotionSummarySubRow(
    item,
    bundleDiscounts,
    promoBundleDiscountOptions
  );
  const promotionSummaryBuyDiscountNet = resolveCartPromotionSummaryBuyDiscountForDisplay(
    item,
    bundleDiscounts,
    promoBundleDiscountOptions
  );
  const promotionSummarySubRowCount = showPromotionSummarySubRow
    ? resolveCartPromotionSummarySubRowCount(item, cartItemsForQtyMax)
    : 0;
  const promotionSummaryUnitDiscount =
    promotionSummarySubRowCount > 1
      ? promotionSummaryBuyDiscountNet / promotionSummarySubRowCount
      : promotionSummaryBuyDiscountNet;
  const inlinePromoDiscountNet = resolveCartLineInlinePromoDiscountForDisplay(item);
  const showInlinePromoDiscount = shouldShowCartLineInlinePromoDiscount(item, cartItemsForQtyMax);
  const showPromotionNameInlineUnderDescription =
    showPromotionNameLabel && !showPromotionSummarySubRow;
  const promoIndicatorLetter =
    normalizedPromoType === "FREE" && showPromoIndicator
      ? "F"
      : (normalizedPromoType === "GET" || normalizedPromoType === "BUY") && showPromoIndicator
        ? "P"
        : isItemPromoted && showPromoIndicator && isPromotionApplied
          ? "P"
          : null;
  const isQuantityDisabled = isOfferLineDisplay;
  const isDisabled =
    isQuantityDisabled ||
    (promotionApplied && isOfferLineDisplay);
  const isDeleteDisabled = isPromoTypeBuy ? false : isDisabled;
  const promoAlternateQtyButtons = isOfferLineDisplay;
  const isPromoFreeOrGetProductName = isOfferLineDisplay;
  const isPromotionLockedNoManualDiscount =
    !promotionExplicitlyRemoved &&
    (promotionApplied || hasValidPromotionId) &&
    (isPromoTypeFree || isGetPromoType || isPromoTypeBuy || isItemPromoted);
  const isDiscountDisabled =
    isDisabled ||
    isFree ||
    isGetPromotionResolved ||
    isGetPromoType ||
    isPromotionLockedNoManualDiscount ||
    (DISABLE_DISC_COLUMN_WHEN_PROMOTED && isItemPromoted && isFreeItem);
  const keyboardHighlightSx = isKeyboardHighlighted ? getCartTableKeyboardHighlightSx() : {};

  const checkEditModeDiscounted = () => {
    const hasManualDiscount = item.manualOverrides?.discount !== undefined && item.manualOverrides?.discount !== null && parseFloat(item.manualOverrides.discount || 0) > 0;
    
    if (hasManualDiscount) {
      return false;
    }

    if (item.promotion_applied === false || item.promotion_applied === "false") {
      return false;
    }

    const promotionApplied = item.promotion_applied === true || item.promotion_applied === "true";
    const promotionId = item.promotion_id !== undefined && item.promotion_id !== null && item.promotion_id !== "";
    const hasPromotionApplied = promotionApplied || promotionId;

    if (!hasPromotionApplied) {
      return false;
    }

    const promoDiscPct = parseFloat(item.promo_disc_percentage ?? 0);
    const promoDiscAmt = parseFloat(item.promo_disc_amount ?? 0);
    if (promoDiscPct === 0 && promoDiscAmt === 0) {
      return false;
    }

    const currentDiscount = parseFloat(item.discount || 0);
    const hasLineDiscount = item.line_discount !== undefined && item.line_discount !== null && parseFloat(item.line_discount || 0) > 0;
    const hasDiscountPercentage = item.discount_percentage !== undefined && item.discount_percentage !== null && parseFloat(item.discount_percentage || 0) > 0;

    if (currentDiscount > 0 && !hasLineDiscount && !hasDiscountPercentage) {
      return false;
    }

    const promoUnitPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null
      ? parseFloat(item.promo_unit_price)
      : null;
    const unitPrice = item.unit_price !== undefined && item.unit_price !== null
      ? parseFloat(item.unit_price)
      : null;
    const baseUnitPrice = item.base_unit_price !== undefined && item.base_unit_price !== null
      ? parseFloat(item.base_unit_price)
      : null;

    if (promoUnitPrice !== null && promoUnitPrice > 0) {
      const referencePrice = unitPrice !== null ? unitPrice : (baseUnitPrice !== null ? baseUnitPrice : null);
      if (referencePrice !== null && referencePrice > 0 && Math.abs(referencePrice - promoUnitPrice) > 0.01) {
        return true;
      }
    }

    if (hasLineDiscount || hasDiscountPercentage) {
      return true;
    }

    return false;
  };

  const showOriginalPrice = checkEditModeDiscounted();

  const getPriceDisplayValue = () => {
    let priceValue;
    if (item.manualOverrides?.price !== undefined) {
      priceValue = parseFloat(item.manualOverrides.price);
    } else if (typeof item.price === "number") {
      priceValue = item.price;
    } else if (typeof item.price === "string" && item.price.length > 0) {
      priceValue = parseFloat(item.price);
    } else {
      priceValue = currentPrice;
    }

    if (Number.isFinite(priceValue)) {
      return String(priceValue);
    }
    return "";
  };

  const [priceInput, setPriceInput] = useState(getPriceDisplayValue);
  const [priceError, setPriceError] = useState(false);
  const [discountError, setDiscountError] = useState(false);
  const getInitialDiscountType = () => {
    if (discount === 0 || discount === "" || discount === null || discount === undefined) {
      return "amount";
    }
    return item.discount_type || item.manualOverrides?.discount_type || item.productWiseDiscountType || "amount";
  };
  const initialDiscountType = getInitialDiscountType();
  const [discountType, setDiscountType] = useState(initialDiscountType);
  const [discountInput, setDiscountInput] = useState(discount === 0 ? "" : String(discount));
  const prevDiscountRef = useRef(discount);
  const prevDiscountTypeRef = useRef(initialDiscountType);
  const userChangedDiscountTypeRef = useRef(false);
  const discountDebounceTimerRef = useRef(null);
  const discountInputDraftRef = useRef("");
  const isDiscountInputFocusedRef = useRef(false);

  const isIncompleteDiscountDraft = (value) => {
    const draft = String(value ?? "").trim();
    return draft === "" || draft === "." || draft.endsWith(".");
  };

  const discountCommentTrimmed = getCartLineDiscountComment(item);

  const requireDiscountCommentIfNeeded = (discountValue) => {
    const num = parseFloat(discountValue);
    if (!Number.isFinite(num) || num <= 0) return true;
    if (!onDiscountCommentChange) return true;
    if (getCartLineDiscountComment(item)) return true;
    const next = window.prompt("Comment is required for discount", "");
    if (next === null) return false;
    const comment = String(next).trim();
    if (!comment) {
      if (onValidationError) {
        onValidationError("Comment is required when discount is applied");
      }
      return false;
    }
    onDiscountCommentChange(item, comment);
    return true;
  };

  const rejectDiscountWithoutComment = () => {
    setDiscountInput("");
    discountInputDraftRef.current = "";
    setDiscountError(true);
    onDiscountChange(item, "0", discountType);
    if (onDiscountChangeComplete) {
      onDiscountChangeComplete(item, "0", discountType);
    }
  };

  const commitDiscountChange = (draft, type = discountType) => {
    if (retainsCartDiscountFocus && isIncompleteDiscountDraft(draft)) return;
    onDiscountChange(item, draft, type);
  };

  const getUnitPriceDisplayValue = () => {
    const val = parseFloat(item.unit_price ?? item.price ?? 0);
    return Number.isFinite(val) ? val.toFixed(2) : "";
  };
  const [unitPriceInput, setUnitPriceInput] = useState(getUnitPriceDisplayValue);
  const [unitPriceError, setUnitPriceError] = useState(false);
  const unitPriceInputFocusedRef = useRef(false);
  const justCommittedUnitPriceRef = useRef(false);
  const tableRowRef = useRef(null);
  const { settings: companySettings, loaded: companySettingsLoaded } = useCompanySettings();
  const isPriceEditableFromSettings = resolveIsPriceEditable(companySettings);
  const isPriceEditableFromSettingsLoaded = companySettingsLoaded;

  useEffect(() => {
    if (!isKeyboardHighlighted || !tableRowRef.current) return;
    tableRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isKeyboardHighlighted]);

  // Tele sales adds new lines at the bottom, so bring the new line into view.
  useEffect(() => {
    if (!isTelecaller || !autoOpenQuantityPopup || !tableRowRef.current) return;
    tableRowRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isTelecaller, autoOpenQuantityPopup]);

  const allowEveUnitPriceEdit =
    !(isTelecaller && isRhp) &&
    !!onPriceChange &&
    (isPriceEditableFromSettingsLoaded ? isPriceEditableFromSettings : true);

  useEffect(() => {
    if (!allowEveUnitPriceEdit || unitPriceInputFocusedRef.current) return;
    if (justCommittedUnitPriceRef.current) {
      justCommittedUnitPriceRef.current = false;
      return;
    }
    setUnitPriceInput(getUnitPriceDisplayValue());
    setUnitPriceError(false);
  }, [item.unit_price, item.price, allowEveUnitPriceEdit]);

  const allotQty = (() => {
    const value = item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ""
      ? item.allot_qty
      : (item.accept_qty !== undefined && item.accept_qty !== null && item.accept_qty !== ""
        ? item.accept_qty
        : String(qty));
    const numValue = String(value).includes(".") ? parseFloat(value) : parseInt(value, 10);
    return Number.isFinite(numValue) && numValue >= 0 ? numValue : qty;
  })();

  const updatedItem = {
    ...item,
    qty: qty,
    unit_price: item.unit_price || item.price,
    discount: discount,
    discount_type: discountType,
    tax_rate: vatRate
  };

  const vatAmount = item.useBackendCalculations && (item.line_vat !== undefined || item.line_tax !== undefined || item.vat !== undefined || item.tax !== undefined)
    ? parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0)
    : calculateItemVat(updatedItem);


  useEffect(() => {
    setPriceInput(getPriceDisplayValue());
    setPriceError(false);
  }, [item.manualOverrides?.price, item.price, currentPrice]);

  useEffect(() => {
    if (userChangedDiscountTypeRef.current) {
      const itemDiscountType = item.discount_type || item.manualOverrides?.discount_type || item.productWiseDiscountType || "amount";
      if (itemDiscountType === discountType) {
        userChangedDiscountTypeRef.current = false;
      }
      return;
    }

    let itemDiscountType = item.discount_type || item.manualOverrides?.discount_type || item.productWiseDiscountType || "amount";

    const currentDiscount = (() => {
      const manualDiscount = item.manualOverrides?.discount;
      if (manualDiscount !== undefined && manualDiscount !== null && manualDiscount !== "") {
        return parseFloat(manualDiscount);
      }
      return parseFloat(item.discount || 0);
    })();

    if (currentDiscount === 0 || !Number.isFinite(currentDiscount)) {
      itemDiscountType = "amount";
    }

    if (itemDiscountType !== discountType) {
      setDiscountType(itemDiscountType);
      prevDiscountTypeRef.current = itemDiscountType;
    }
  }, [item.discount_type, item.manualOverrides?.discount_type, item.productWiseDiscountType, item.discount, item.manualOverrides?.discount, discountType]);

  useEffect(() => {
    if (retainsCartDiscountFocus && isDiscountInputFocusedRef.current) return;
    const currentDiscount = (() => {
      const manualDiscount = item.manualOverrides?.discount;
      if (manualDiscount !== undefined && manualDiscount !== null && manualDiscount !== "") {
        const value = parseFloat(manualDiscount);
        return Number.isFinite(value) && value >= 0 ? value : 0;
      }
      if (item.discount !== undefined && item.discount !== null && item.discount !== "") {
        const value = parseFloat(item.discount);
        return Number.isFinite(value) && value >= 0 ? value : 0;
      }
      const apiItemDiscount = item.item_discount !== undefined && item.item_discount !== null && item.item_discount !== "" ? parseFloat(item.item_discount) : null;
      if (apiItemDiscount !== null && Number.isFinite(apiItemDiscount) && apiItemDiscount >= 0) {
        return apiItemDiscount;
      }
      return 0;
    })();
    if (prevDiscountRef.current !== currentDiscount) {
      const newDiscount = currentDiscount === 0 ? "" : String(currentDiscount);
      setDiscountInput(newDiscount);
      prevDiscountRef.current = currentDiscount;
    }
  }, [item.discount, item.item_discount, item.manualOverrides?.discount, discount]);

  const getLiveDiscountForTotal = () => {
    if (discountInput === "" || discountInput === null || discountInput === undefined) {
      return 0;
    }
    const parsed = parseFloat(String(discountInput).trim());
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : discount;
  };

  const lineQtyForTotal = parseQty(item, allotQty);

  const calculateTotal = () => {
    const netTotal = resolveCartLineDisplayTotal(item, lineQtyForTotal, {
      discount: getLiveDiscountForTotal(),
      discountType,
      isEditMode: true,
    });
    return resolveCartLineInlinePromoGrossDisplayTotal(netTotal);
  };

  const vatPercentDisplay = `${Number.isFinite(vatRate) ? vatRate.toFixed(2) : "0.00"}%`;
  const vatAmountDisplay = `£${vatAmount.toFixed(2)}`;
  const taxCodeDisplay = (() => {
    const own = resolveProductTaxCode(item);
    if (own) return own;
    const pid = String(item.product_id ?? item.productId ?? "");
    if (!pid || !Array.isArray(cartItemsForQtyMax)) return "-";
    for (let i = 0; i < cartItemsForQtyMax.length; i += 1) {
      const line = cartItemsForQtyMax[i];
      if (String(line?.product_id ?? line?.productId ?? "") !== pid) continue;
      const code = resolveProductTaxCode(line);
      if (code) return code;
    }
    return "-";
  })();
  const vatDisplayValue = isTelecaller
    ? taxCodeDisplay
    : showVatAsAmount
      ? vatAmountDisplay
      : vatPercentDisplay;

  const allowDecimal = allowsDecimalQty(item);
  const maxQty = allowDecimal ? 9999.99 : 9999;
  const minQty = allowDecimal ? 0.01 : 0;

  const handleAllotQtyChange = (newValue) => {
    if (!onAllotQtyChange) {
      return false;
    }
    return onAllotQtyChange(item, String(newValue)) !== false;
  };

  const qtyControlMax = !shouldSkipTelecallerStockLimit(item)
    ? getQtyControlMax(item, "allot", cartItemsForQtyMax)
    : isMeatsProduct(item)
      ? 9999.99
      : 9999;

  const handleIncrement = (delta) => {
    const newValue = allotQty + delta;
    return handleAllotQtyChange(newValue) !== false;
  };

  const handleDecrement = (delta) => {
    const newValue = allotQty + delta;
    if (newValue < minQty) {
      return false;
    }
    return handleAllotQtyChange(newValue) !== false;
  };

  return (
    <>
    <TableRow
      ref={tableRowRef}
      hover
      data-cart-row={index}
      sx={{ 
        '& .MuiTableCell-root': { p: 0.7 }, 
        opacity: isLoading ? 0.5 : 1,
        ...(shouldShowRedText && {
          color: 'error.main',
          '& .MuiTypography-root': {
            color: 'error.main'
          },
          '& .MuiInputBase-input': {
            color: 'error.main'
          }
        }),
        ...keyboardHighlightSx,
      }}
    >
      <TableCell sx={{ fontSize: '0.7rem' }} align="center">
        <Typography className="cartData">
          {index + 1}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontSize: '0.7rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography className="cartData">
          {code}
        </Typography>
       
        {promoIndicatorLetter === "F" && (
          <Typography
            variant="caption"
            fontSize="0.65rem"
            fontWeight={500}
            color="info.darker"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            <Iconify icon="solar:check-circle-bold" width={12} />
            (F)
          </Typography>
        )}
        {promoIndicatorLetter === "P" && (
          <Typography
            variant="caption"
            fontSize="0.65rem"
            fontWeight={500}
            color="info.darker"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5
            }}
          >
            <Iconify icon="solar:check-circle-bold" width={12} />
            (P)
          </Typography>
        )}
        </Box>
      </TableCell>

      <TableCell sx={{ maxWidth: 200 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                ...(shouldShowRedText
                  ? { color: 'error.main' }
                  : isPromoFreeOrGetProductName
                    ? { color: '#0456f1' }
                    : {}),
              }}
              title={description}
            >
              {description}
            </Typography>
            {isContractPrice && (
              <Chip
                label="C"
                size="small"
                color="primary"
                sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
              />
            )}
          </Box>
          {showPromotionNameInlineUnderDescription ? (
            <Typography
              variant="caption"
              color="#0456f1"
              fontWeight={500}
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
              title={promotionNameTrimmed}
            >
              {promotionNameTrimmed}
            </Typography>
          ) : null}
          {discountCommentTrimmed ? (
            <Typography
              variant="caption"
              fontWeight={400}
              color="text.secondary"
              sx={{
                fontSize: '0.7rem',
                fontStyle: 'italic',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={discountCommentTrimmed}
            >
              {discountCommentTrimmed}
            </Typography>
          ) : null}
        </Box>
      </TableCell>
      <TableCell sx={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>
        {/* {canSplit ? (
          <Select
            value={orderType}
            onChange={(e) => onTypeChange(item, e.target.value)}
            size="small"
            disabled={isDisabled}
            sx={{
              minWidth: 70,
              '& .MuiSelect-select': {
                fontSize: '0.7rem',
                py: 0.2,
                px: 0.75,
                textAlign: 'left',
                textOverflow: 'unset',
                overflow: 'visible',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                maxWidth: 'none'
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)'
              }
            }}
          >
            <MenuItem value="Box" sx={{ fontSize: '0.7rem' }}>Box</MenuItem>
            <MenuItem value="Piece" sx={{ fontSize: '0.7rem' }}>Piece</MenuItem>
          </Select>
        ) : ( */}
          <Typography className="cartData">
            {orderType}
          </Typography>
        
      </TableCell>
      <TableCell align="center" px={1}>
        <Box
          data-cart-qty-scope={index}
          onFocusCapture={
            typeof onQtyRegionFocusChange === "function"
              ? () => onQtyRegionFocusChange(true)
              : undefined
          }
        >
          <QuantityControl
            value={allotQty}
            onChange={handleAllotQtyChange}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            min={isMeatsProduct(item) ? 0.01 : 0}
            max={qtyControlMax}
            size="small"
            disabled={isQuantityDisabled}
            autoOpen={autoOpenQuantityPopup}
            onPopupOpen={onQuantityPopupOpen}
            onPopupClose={onQuantityPopupClose}
            orderType={orderType}
            canSplit={canSplit}
            onOrderTypeChange={(newOrderType) => onTypeChange(item, newOrderType)}
            allowDecimal={isMeatsProduct(item)}
            isPromoTypeFree={isOfferLineDisplay && isPromoTypeFree}
            promoAlternateQtyButtons={promoAlternateQtyButtons}
            onPromoPButtonClick={
              promoAlternateQtyButtons && typeof onPromoQtyLineOpenDrawer === "function"
                ? () => onPromoQtyLineOpenDrawer(item)
                : undefined
            }
            onPromoCPButtonClick={
              promoAlternateQtyButtons && typeof onPromoQtyOpenPromotionsModal === "function"
                ? () => onPromoQtyOpenPromotionsModal(item)
                : undefined
            }
          />
        </Box>
      </TableCell>
      {isTelecaller ? (
        <>
          <TableCell align="right">
            {(() => {
              const orderType = item.order_type || item.type || "Box";
              const stdPriceRaw = item.standard_price ?? (orderType === "Box" ? item.sales_box_price : item.sales_piece_price) ?? item.standard_price ?? 0;
              const stdPrice = Number.isFinite(parseFloat(stdPriceRaw)) && parseFloat(stdPriceRaw) >= 0 ? parseFloat(stdPriceRaw) : 0;
              
              return (
                <Typography className="cartData">
                  £{stdPrice.toFixed(2)}
                </Typography>
              );
            })()}
          </TableCell>
          <TableCell align="right">
            {allowEveUnitPriceEdit ? (
              <TextField
                value={unitPriceInput}
                onFocus={(e) => {
                  unitPriceInputFocusedRef.current = true;
                  e.target.select();
                }}
                onBlur={() => {
                  unitPriceInputFocusedRef.current = false;
                  const trimmed = unitPriceInput.trim();
                  if (trimmed === "" || trimmed === ".") {
                    const fallback = getUnitPriceDisplayValue() || "0.00";
                    setUnitPriceInput(fallback);
                    setUnitPriceError(false);
                    onPriceChange(item, fallback, { commit: true });
                    return;
                  }
                  const parsed = parseFloat(trimmed);
                  if (!Number.isFinite(parsed) || parsed < 0.01 || parsed > 99999.99) {
                    setUnitPriceError(true);
                    const reset = getUnitPriceDisplayValue();
                    setUnitPriceInput(reset);
                    setUnitPriceError(false);
                    onPriceChange(item, reset, { commit: true });
                    if (onValidationError) onValidationError("Price must be between £0.01 and £99,999.99");
                    return;
                  }
                  const formatted = parsed.toFixed(2);
                  setUnitPriceInput(formatted);
                  setUnitPriceError(false);
                  justCommittedUnitPriceRef.current = true;
                  onPriceChange(item, formatted, { commit: true });
                }}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || value === ".") {
                    setUnitPriceInput(value);
                    setUnitPriceError(false);
                    onPriceChange(item, value, { commit: false });
                    return;
                  }
                  if (!/^\d*\.?\d*$/.test(value)) return;
                  const parts = value.split(".");
                  if (parts.length > 1 && parts[1].length > 2) return;
                  const num = parseFloat(value);
                  if (!isNaN(num) && num <= 99999.99) {
                    setUnitPriceInput(value);
                    setUnitPriceError(false);
                    onPriceChange(item, value, { commit: false });
                  } else if (num > 99999.99) {
                    setUnitPriceError(true);
                    if (onValidationError) onValidationError("Price must be between £0.01 and £99,999.99");
                  }
                }}
                size="small"
                error={unitPriceError}
                type="text"
                disabled={isDisabled}
                inputProps={{ style: { textAlign: "right", fontSize: "0.7rem" } }}
                sx={{ width: 78, 
                  "& .MuiInputBase-root": { height: 28 },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#000000 !important",
                  },
                }}
              />
            ) : (
              (() => {
                const hasPromoPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null;
                const hasUnitPrice = item.unit_price !== undefined && item.unit_price !== null;
                let displayPrice = 0;
                let originalPrice = null;
                let shouldShowStrikethrough = false;
                if (item.useBackendCalculations) {
                  const itemUnitPrice = resolveCartLineStrikethroughPrice(item);
                  if (hasPromoPrice) {
                    const promoPrice = parseFloat(item.promo_unit_price);
                    if (isPromoTypeFree) {
                      displayPrice = Number.isFinite(promoPrice) ? promoPrice : 0;
                    } else {
                      displayPrice = promoPrice;
                    }
                    const shouldShowOfferStrikethrough =
                      !isPromoTypeBuy &&
                      itemUnitPrice > 0 &&
                      displayPrice < itemUnitPrice - 0.01 &&
                      (isPromoTypeFree || isGetPromoType || isFreeItem || isGetPromotion || displayPrice < 0.01);
                    if (shouldShowOfferStrikethrough) {
                      originalPrice = itemUnitPrice;
                      shouldShowStrikethrough = true;
                    }
                  } else if (hasUnitPrice) {
                    displayPrice = parseFloat(item.unit_price);
                  }
                } else {
                  const numValue = parseFloat(priceInput);
                  displayPrice = Number.isFinite(numValue) ? numValue : 0;
                }
                if (shouldShowStrikethrough && originalPrice !== null) {
                  return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right', gap: 0.25 }}>
                      <Typography className="cartData" sx={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '0.65rem' }}>
                        £{originalPrice.toFixed(2)}
                      </Typography>
                      <Typography className="cartData">
                        £{displayPrice.toFixed(2)}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Typography className="cartData">
                    £{displayPrice.toFixed(2)}
                  </Typography>
                );
              })()
            )}
          </TableCell>
        </>
      ) : (
        <TableCell align="right">
          {allowEveUnitPriceEdit ? (
            <TextField
              value={unitPriceInput}
              onFocus={(e) => {
                unitPriceInputFocusedRef.current = true;
                e.target.select();
              }}
              onBlur={() => {
                unitPriceInputFocusedRef.current = false;
                const trimmed = unitPriceInput.trim();
                if (trimmed === "" || trimmed === ".") {
                  const fallback = getUnitPriceDisplayValue() || "0.00";
                  setUnitPriceInput(fallback);
                  setUnitPriceError(false);
                  onPriceChange(item, fallback, { commit: true });
                  return;
                }
                const parsed = parseFloat(trimmed);
                if (!Number.isFinite(parsed) || parsed < 0.01 || parsed > 99999.99) {
                  setUnitPriceError(true);
                  const reset = getUnitPriceDisplayValue();
                  setUnitPriceInput(reset);
                  setUnitPriceError(false);
                  onPriceChange(item, reset, { commit: true });
                  if (onValidationError) onValidationError("Price must be between £0.01 and £99,999.99");
                  return;
                }
                const formatted = parsed.toFixed(2);
                setUnitPriceInput(formatted);
                setUnitPriceError(false);
                justCommittedUnitPriceRef.current = true;
                onPriceChange(item, formatted, { commit: true });
              }}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || value === ".") {
                  setUnitPriceInput(value);
                  setUnitPriceError(false);
                  onPriceChange(item, value, { commit: false });
                  return;
                }
                if (!/^\d*\.?\d*$/.test(value)) return;
                const parts = value.split(".");
                if (parts.length > 1 && parts[1].length > 2) return;
                const num = parseFloat(value);
                if (!isNaN(num) && num <= 99999.99) {
                  setUnitPriceInput(value);
                  setUnitPriceError(false);
                  onPriceChange(item, value, { commit: false });
                } else if (num > 99999.99) {
                  setUnitPriceError(true);
                  if (onValidationError) onValidationError("Price must be between £0.01 and £99,999.99");
                }
              }}
              size="small"
              error={unitPriceError}
              type="text"
              disabled={isDisabled}
              inputProps={{ style: { textAlign: "right", fontSize: "0.7rem" } }}
              sx={{ width: 87, "& .MuiInputBase-root": { height: 27 }, "& .MuiOutlinedInput-notchedOutline": { borderColor: "#000 !important" } }}
            />
          ) : (
            (() => {
              const hasPromoPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null;
              const hasUnitPrice = item.unit_price !== undefined && item.unit_price !== null;
              let displayPrice = 0;
              let originalPrice = null;
              let shouldShowStrikethrough = false;
              if (item.useBackendCalculations) {
                const itemUnitPrice = resolveCartLineStrikethroughPrice(item);
                if (hasPromoPrice) {
                  const promoPrice = parseFloat(item.promo_unit_price);
                  if (isPromoTypeFree) {
                    displayPrice = Number.isFinite(promoPrice) ? promoPrice : 0;
                  } else {
                    displayPrice = promoPrice;
                  }
                  const shouldShowOfferStrikethrough =
                    !isPromoTypeBuy &&
                    itemUnitPrice > 0 &&
                    displayPrice < itemUnitPrice - 0.01 &&
                    (isPromoTypeFree || isGetPromoType || isFreeItem || isGetPromotion || displayPrice < 0.01);
                  if (shouldShowOfferStrikethrough) {
                    originalPrice = itemUnitPrice;
                    shouldShowStrikethrough = true;
                  }
                } else if (hasUnitPrice) {
                  displayPrice = parseFloat(item.unit_price);
                }
              } else {
                const numValue = parseFloat(priceInput);
                displayPrice = Number.isFinite(numValue) ? numValue : 0;
              }
              if (shouldShowStrikethrough && originalPrice !== null) {
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right', gap: 0.25 }}>
                    <Typography className="cartData" sx={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '0.65rem' }}>
                      £{originalPrice.toFixed(2)}
                    </Typography>
                    <Typography className="cartData">
                      £{displayPrice.toFixed(2)}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Typography className="cartData">
                  £{displayPrice.toFixed(2)}
                </Typography>
              );
            })()
          )}
        </TableCell>
      )}
      <TableCell align="center" {...(retainsCartDiscountFocus ? { "data-cart-discount-input": true } : {})}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <TextField
          value={isDiscountDisabled ? "" : discountInput}
          onFocus={(e) => {
            if (retainsCartDiscountFocus) {
              isDiscountInputFocusedRef.current = true;
            }
            e.target.select();
          }}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          onChange={(e) => {
            const value = e.target.value;

            if (value === "" || value === ".") {
              if (discountDebounceTimerRef.current) {
                clearTimeout(discountDebounceTimerRef.current);
                discountDebounceTimerRef.current = null;
              }
              setDiscountInput(value);
              discountInputDraftRef.current = value;
              setDiscountError(false);
              if (retainsCartDiscountFocus) {
                if (value === "") {
                  commitDiscountChange("0", discountType);
                }
              } else {
                onDiscountChange(item, "0", discountType);
              }
              return;
            }

            if (discountType === "percentage") {
              const numericValue = value.replace(/[^0-9.]/g, '');
              const numValue = parseFloat(numericValue);

              if (!isNaN(numValue) && numValue > 100) {
                setDiscountError(true);
                if (onValidationError) {
                  onValidationError("Discount cannot exceed 100%");
                }
                return;
              }

              setDiscountInput(numericValue);
              discountInputDraftRef.current = numericValue;
              setDiscountError(false);

              if (discountDebounceTimerRef.current) {
                clearTimeout(discountDebounceTimerRef.current);
              }

              discountDebounceTimerRef.current = setTimeout(() => {
                if (retainsCartDiscountFocus) {
                  commitDiscountChange(discountInputDraftRef.current, discountType);
                } else if (!isNaN(numValue) && numValue >= 0) {
                  onDiscountChange(item, numericValue, discountType);
                }
                discountDebounceTimerRef.current = null;
              }, retainsCartDiscountFocus ? 500 : 0);
            } else {
              const numValue = parseFloat(value);

              if (!isNaN(numValue) && numValue >= 0) {
                const isFixedDiscount = discountType === "amount" || discountType === "£" || discountType === "fixed";
                if (isFixedDiscount) {
                  let unitPrice = 0;
                  if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "") {
                    unitPrice = parseFloat(item.unit_price);
                  } else if (orderType === "Box") {
                    unitPrice = parseFloat(item.sales_box_price || 0);
                  } else {
                    unitPrice = parseFloat(item.sales_piece_price || 0);
                  }
                  if (unitPrice > 0 && numValue > unitPrice) {
                    setDiscountError(true);
                    if (onValidationError) {
                      onValidationError(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
                    }
                    return;
                  }
                }

                setDiscountInput(value);
                discountInputDraftRef.current = value;
                setDiscountError(false);

                if (discountDebounceTimerRef.current) {
                  clearTimeout(discountDebounceTimerRef.current);
                }

                discountDebounceTimerRef.current = setTimeout(() => {
                  if (retainsCartDiscountFocus) {
                    commitDiscountChange(discountInputDraftRef.current, discountType);
                  } else {
                    onDiscountChange(item, value, discountType);
                  }
                  discountDebounceTimerRef.current = null;
                }, retainsCartDiscountFocus ? 500 : 0);
              } else {
                setDiscountInput(value);
                setDiscountError(true);
              }
            }
          }}
          onBlur={(e) => {
            if (retainsCartDiscountFocus) {
              isDiscountInputFocusedRef.current = false;
            }
            if (discountDebounceTimerRef.current) {
              clearTimeout(discountDebounceTimerRef.current);
              discountDebounceTimerRef.current = null;
            }

            const value = e.target.value.trim();

            if (value === "" || value === ".") {
              setDiscountInput("");
              setDiscountError(false);
              onDiscountChange(item, "0", discountType);
              if (onDiscountChangeComplete) {
                onDiscountChangeComplete(item, "0", discountType);
              }
              return;
            }

            const numValue = parseFloat(value);
            const maxPct = discountType === "percentage" ? 100 : undefined;

            if (!Number.isFinite(numValue) || numValue < 0) {
              const currentDiscountValue = discount === 0 ? "" : String(discount);
              setDiscountInput(currentDiscountValue);
              setDiscountError(true);
              if (onValidationError) {
                onValidationError(discountType === "percentage" ? "Please enter a valid discount percentage" : "Please enter a valid discount amount");
              }
              return;
            }

            if (maxPct !== undefined && numValue > maxPct) {
              const currentDiscountValue = discount === 0 ? "" : String(discount);
              setDiscountInput(currentDiscountValue);
              setDiscountError(true);
              if (onValidationError) {
                onValidationError("Discount cannot exceed 100%");
              }
              return;
            }

            const decimalPlaces = (value.split('.')[1] || '').length;
            if (decimalPlaces > 2) {
              const currentDiscountValue = discount === 0 ? "" : String(discount);
              setDiscountInput(currentDiscountValue);
              setDiscountError(true);
              if (onValidationError) {
                onValidationError("Discount can have maximum 2 decimal places");
              }
              return;
            }

            const isFixedDiscount = discountType === "amount" || discountType === "£" || discountType === "fixed";
            if (isFixedDiscount && Number.isFinite(numValue) && numValue >= 0) {
              let unitPrice = 0;
              if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "") {
                unitPrice = parseFloat(item.unit_price);
              } else if (orderType === "Box") {
                unitPrice = parseFloat(item.sales_box_price || 0);
              } else {
                unitPrice = parseFloat(item.sales_piece_price || 0);
              }
              if (unitPrice > 0 && numValue > unitPrice) {
                const currentDiscountValue = discount === 0 ? "" : String(discount);
                setDiscountInput(currentDiscountValue);
                setDiscountError(true);
                if (onValidationError) {
                  onValidationError(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
                }
                return;
              }
            }

            const formattedValue = parseFloat(value).toString();
            const formattedNum = parseFloat(formattedValue);
            const currentDiscountNum = parseFloat(discount);
            const isExplicitZero = formattedNum === 0;
            const hasChanged = Math.abs(formattedNum - currentDiscountNum) >= 0.01;

            if (!isExplicitZero && Number.isFinite(formattedNum) && Number.isFinite(currentDiscountNum) && !hasChanged) {
              if (formattedNum > 0 && !requireDiscountCommentIfNeeded(formattedValue)) {
                rejectDiscountWithoutComment();
                return;
              }
              setDiscountInput(formattedValue);
              setDiscountError(false);
              if (onDiscountChangeComplete) {
                onDiscountChangeComplete(item, formattedValue, discountType);
              }
              return;
            }

            if (formattedNum > 0 && !requireDiscountCommentIfNeeded(formattedValue)) {
              rejectDiscountWithoutComment();
              return;
            }

            setDiscountInput(formattedValue);
            discountInputDraftRef.current = formattedValue;
            setDiscountError(false);
            if (retainsCartDiscountFocus) {
              commitDiscountChange(formattedValue, discountType);
            } else {
              onDiscountChange(item, formattedValue, discountType);
            }

            if (onDiscountChangeComplete) {
              onDiscountChangeComplete(item, formattedValue, discountType);
            }
          }}
          size="small"
          disabled={isDiscountDisabled}
          error={discountError}
          type="number"
          placeholder="0"
          
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Select
                  value={discountType}
                  onChange={(e) => {
                    const newType = e.target.value;
                    const oldType = discountType;

                    if (oldType !== newType) {
                      const currentValue = discountInput;
                      const isEmptyOrZero = !currentValue || currentValue.trim() === "" || currentValue === "0";

                      const isNewTypeAmount = newType === "amount" || newType === "£" || newType === "fixed";
                      if (!isEmptyOrZero && isNewTypeAmount) {
                        const numValue = parseFloat(currentValue);
                        if (Number.isFinite(numValue) && numValue >= 0) {
                          let unitPrice = 0;
                          if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "") {
                            unitPrice = parseFloat(item.unit_price);
                          } else if (orderType === "Box") {
                            unitPrice = parseFloat(item.sales_box_price || 0);
                          } else {
                            unitPrice = parseFloat(item.sales_piece_price || 0);
                          }
                          if (unitPrice > 0 && numValue > unitPrice) {
                            setDiscountError(true);
                            if (onValidationError) {
                              onValidationError(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
                            }
                            return;
                          }
                        }
                      }

                      setDiscountType(newType);
                      prevDiscountTypeRef.current = newType;
                      userChangedDiscountTypeRef.current = true;
                      setDiscountError(false);
                      setDiscountInput(currentValue);
                      if (!isEmptyOrZero) {
                        if (!requireDiscountCommentIfNeeded(currentValue)) {
                          rejectDiscountWithoutComment();
                          return;
                        }
                        onDiscountChange(item, currentValue, newType);
                        if (onDiscountChangeComplete) {
                          onDiscountChangeComplete(item, currentValue, newType);
                        }
                      }
                    }
                  }}
                  size="small"
                  disabled={isDiscountDisabled}
                  sx={{
                    height: 12,
                    fontSize: '0.7rem',
                    minWidth: 32,
                    width: 32,
                    px: 0.5,
                    '& .MuiSelect-select': {
                      py: 0,
                      px: 0,
                      fontSize: '0.7rem',
                      minHeight: 'auto',
                      textAlign: 'center',
                      textOverflow: 'clip',
                      overflow: 'visible'
                    },
                    '& .MuiSelect-icon': {
                      fontSize: '0.55rem',
                      right: 5
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                
                    },
                    '& fieldset': {
                      border: 'none'
                    }
                  }}
                >
                  <MenuItem value="amount" sx={{ fontSize: '0.7rem', px: 0.75, justifyContent: 'center' }}>£</MenuItem>
                  <MenuItem value="percentage" sx={{ fontSize: '0.7rem', px: 0.75, justifyContent: 'center' }}>%</MenuItem>
                </Select>
              </InputAdornment>
            )
          }}
          inputProps={{
            ...(retainsCartDiscountFocus ? { "data-cart-discount-input": true } : {}),
            step: "0.01",
            min: "0",
            max: discountType === "percentage" ? "100" : undefined,
            style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px 4px' }
          }}
          sx={{
            minWidth: 70,
            width: 90,
            '& .MuiInputBase-root': {
              height: 26,
              fontSize: '0.7rem'
            },
            '& .MuiOutlinedInput-root': {
              padding: 0
            },
            '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
              opacity: 1
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#000 !important",
            },
          }}
        />
        {onDiscountCommentChange ? (
          <IconButton
            size="small"
            title={discountCommentTrimmed || "Add discount comment"}
            onClick={() => {
              const next = window.prompt(
                parseFloat(discount) > 0 ? "Comment is required for discount" : "Discount comment",
                discountCommentTrimmed
              );
              if (next === null) return;
              const comment = String(next).trim();
              if (!comment && parseFloat(discount) > 0) {
                if (onValidationError) {
                  onValidationError("Comment is required when discount is applied");
                }
                return;
              }
              onDiscountCommentChange(item, next);
            }}
            sx={{ padding: "2px", marginLeft: "2px" }}
          >
            <Iconify
              icon={discountCommentTrimmed ? "mdi:comment-text" : "mdi:comment-outline"}
              width={16}
              height={16}
              sx={{ color: discountCommentTrimmed ? "#0456f1" : "text.disabled" }}
            />
          </IconButton>
        ) : null}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography className="cartData">
            £{calculateTotal().toFixed(2)}
          </Typography>
          {showInlinePromoDiscount ? (
            <Typography sx={{ color: '#0456f1 !important', fontWeight: 600, fontSize: '0.75rem' }}>
              -£{inlinePromoDiscountNet.toFixed(2)}
            </Typography>
          ) : null}
        </Box>
      </TableCell>
     
      <TableCell sx={{minWidth: 50, whiteSpace: 'nowrap' }} align="right">
        {isTelecaller ? (
          <Tooltip arrow placement="top" title={`VAT: ${vatAmountDisplay} (${vatPercentDisplay})`}>
            <Typography className="cartData" component="span">
              {vatDisplayValue}
            </Typography>
          </Tooltip>
        ) : (
          <Typography className="cartData">
            {vatDisplayValue}
          </Typography>
        )}
      </TableCell>
      
      <TableCell sx={{ textAlign: 'center'}}>
        <IconButton
          onClick={() => onDelete(item.id)}
          size="small"
          disabled={isDeleteDisabled}
          sx={{ color: "error.main" }}
        >
          <Iconify icon="mdi:close" width={18} height={18} />
        </IconButton>
      </TableCell>
    </TableRow>
    {promotionSummarySubRowCount > 0
      ? Array.from({ length: promotionSummarySubRowCount }, (_, promoRowIndex) => (
      <TableRow
        key={`${item.id || index}-promo-summary-${promoRowIndex}`}
        data-cart-row={index}
        sx={{
          '& .MuiTableCell-root': {
            p: 0.4,
            borderTop: 0,
          },
          ...keyboardHighlightSx,
        }}
      >
        <TableCell />
        <TableCell />
        <TableCell sx={{ maxWidth: 200 }}>
          <Typography
            variant="caption"
            color="#0456f1"
            fontWeight={500}
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textTransform: 'uppercase',
            }}
            title={promotionNameTrimmed}
          >
            {promotionNameTrimmed}
          </Typography>
        </TableCell>
        <TableCell />
        <TableCell align="center" />
        {isTelecaller ? (
          <>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell align="right">
              <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ color: '#0456f1' }}>
                -£{promotionSummaryUnitDiscount.toFixed(2)}
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell />
          </>
        ) : (
          <>
            <TableCell />
            <TableCell />
            <TableCell align="right">
              <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ color: '#0456f1' }}>
                -£{promotionSummaryUnitDiscount.toFixed(2)}
              </Typography>
            </TableCell>
            <TableCell />
            <TableCell />
          </>
        )}
      </TableRow>
    ))
    : null}
    </>
  );
};

const areEqual = (prevProps, nextProps) => {
  const prevItem = prevProps.item;
  const nextItem = nextProps.item;

  const prevCode = prevItem.code || prevItem.product_code || "";
  const nextCode = nextItem.code || nextItem.product_code || "";
  const prevDesc =
    prevItem.desc || prevItem.name || prevItem.product_name || "";
  const nextDesc =
    nextItem.desc || nextItem.name || nextItem.product_name || "";

  return (
    prevItem.id === nextItem.id &&
    prevCode === nextCode &&
    prevDesc === nextDesc &&
    prevItem.qty === nextItem.qty &&
    prevItem.price === nextItem.price &&
    prevItem.discount === nextItem.discount &&
    prevItem.discount_comment === nextItem.discount_comment &&
    prevItem.comment === nextItem.comment &&
    prevItem.discount_type === nextItem.discount_type &&
    prevItem.order_type === nextItem.order_type &&
    prevItem.type === nextItem.type &&
    prevItem.vat === nextItem.vat &&
    prevItem.tax === nextItem.tax &&
    prevItem.sub_total === nextItem.sub_total &&
    prevItem.promo_type === nextItem.promo_type &&
    prevItem.promotion_applied === nextItem.promotion_applied &&
    prevItem.promotion_name === nextItem.promotion_name &&
    prevItem.promotionName === nextItem.promotionName &&
    prevItem.promo_name === nextItem.promo_name &&
    prevItem.showPromo === nextItem.showPromo &&
    prevItem.generated === nextItem.generated &&
    prevItem.manualOverrides?.price === nextItem.manualOverrides?.price &&
    prevItem.manualOverrides?.discount === nextItem.manualOverrides?.discount &&
    prevItem.manualOverrides?.discount_comment === nextItem.manualOverrides?.discount_comment &&
    prevItem.manualOverrides?.discount_type === nextItem.manualOverrides?.discount_type &&
    prevProps.index === nextProps.index &&
    prevProps.isKeyboardHighlighted === nextProps.isKeyboardHighlighted &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.customerId === nextProps.customerId &&
    prevProps.isTelecaller === nextProps.isTelecaller &&
    prevItem.allot_qty === nextItem.allot_qty &&
    prevProps.bundleDiscounts === nextProps.bundleDiscounts &&
    prevProps.bundleLineDiscountByKey === nextProps.bundleLineDiscountByKey
  );
};

export default memo(CartTableRowEposSales, areEqual);

